/**
 * ToolConfirmModal — inline confirmation prompt rendered in the chat
 * surface when the model requests a non-read-only tool.
 *
 * Sprint AI-MODULE v2.2 AI-52. The chat_service yields a
 * ``tool_confirmation_required`` SSE event; the chat UI mounts this
 * component until the user clicks Confirm or Cancel. The decision is
 * posted to ``/mod/ai/chat/threads/{tid}/confirm-tool`` which writes
 * to a Redis key the chat_service is polling. On user response the
 * stream resumes — either firing the tool (Confirm) or returning a
 * cancelled tool_result to the model (Cancel).
 *
 * The modal renders inline in the message column, not as an overlay,
 * because (a) the chat surface is the user's working context and we
 * shouldn't dim it, and (b) the bubble surface has no room for an
 * overlay.
 */
import { useState, type FC } from 'react';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';

export interface PendingConfirmation {
  tool_use_id: string;
  slug: string;
  name: string;
  description: string;
  side_effect: 'write' | 'destructive' | string;
  args: Record<string, unknown>;
}

interface Props {
  pending: PendingConfirmation;
  onDecide: (
    decision: 'confirm' | 'cancel',
    argumentOverrides?: Record<string, unknown>,
  ) => Promise<void>;
}

const ToolConfirmModal: FC<Props> = ({ pending, onDecide }) => {
  const [busy, setBusy] = useState(false);

  const isDestructive = pending.side_effect === 'destructive';

  const handle = async (decision: 'confirm' | 'cancel') => {
    if (busy) return;
    setBusy(true);
    try {
      await onDecide(decision);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={
        'ai-tool-confirm' + (isDestructive ? ' is-destructive' : '')
      }
      role="dialog"
      aria-label={`Confirm ${pending.name}`}
    >
      <div className="ai-tool-confirm__head">
        {isDestructive ? (
          <AlertTriangle size={16} className="ai-tool-confirm__icon-warn" />
        ) : (
          <CheckCircle2 size={16} className="ai-tool-confirm__icon-ok" />
        )}
        <span className="ai-tool-confirm__title">
          The assistant wants to {isDestructive ? 'run a destructive action' : 'take an action on your behalf'}
        </span>
      </div>
      <div className="ai-tool-confirm__body">
        <div className="ai-tool-confirm__tool">
          <strong>{pending.name}</strong>{' '}
          <code className="ai-tool-confirm__slug">{pending.slug}</code>
        </div>
        <div className="ai-tool-confirm__desc">{pending.description}</div>
        {Object.keys(pending.args).length > 0 && (
          <dl className="ai-tool-confirm__args">
            {Object.entries(pending.args).map(([k, v]) => (
              <div key={k} className="ai-tool-confirm__arg-row">
                <dt>{k}</dt>
                <dd>{formatValue(v)}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
      <div className="ai-tool-confirm__actions">
        <button
          type="button"
          className={'btn btn--sm ' + (isDestructive ? 'btn--danger' : 'btn--primary')}
          disabled={busy}
          onClick={() => handle('confirm')}
        >
          <CheckCircle2 size={12} /> {isDestructive ? 'Yes, run it' : 'Confirm + run'}
        </button>
        <button
          type="button"
          className="btn btn--sm btn--ghost"
          disabled={busy}
          onClick={() => handle('cancel')}
        >
          <X size={12} /> Cancel
        </button>
      </div>
    </div>
  );
};

export default ToolConfirmModal;


function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}
